'use client';

import React, { useState } from 'react';
import { useRouter } from 'next/navigation';
import { toast } from 'sonner';
import Icon from '@/components/ui/AppIcon';
import OtpModal from './OtpModal';
import { useAuth } from '@/contexts/AuthContext';

export default function SellerRegisterForm() {
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [nameError, setNameError] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [otpOpen, setOtpOpen] = useState(false);
  const router = useRouter();
  const { signInWithPhone } = useAuth();

  const validate = () => {
    const nErr = name.trim() ? '' : 'Name is required';
    const pErr = !phone ? 'Mobile number is required' : !/^\d{10}$/.test(phone) ? 'Enter a valid 10-digit mobile number' : '';
    setNameError(nErr);
    setPhoneError(pErr);
    return !nErr && !pErr;
  };

  const handleSendOtp = async () => {
    if (!validate()) return;
    setIsLoading(true);
    try {
      await signInWithPhone(phone);
      toast.success(`OTP sent to +91 ${phone}`);
      setOtpOpen(true);
    } catch (err: any) {
      toast.error(err?.message || 'Failed to send OTP. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleOtpVerified = async () => {
    setOtpOpen(false);
    toast.success('Seller account created! Welcome to LocalMandi.');
    await new Promise((r) => setTimeout(r, 400));
    router.push('/seller-dashboard');
  };

  return (
    <>
      <div className="space-y-5">
        {/* Info banner */}
        <div className="rounded-lg border border-[#FF9900]/40 bg-[#fff3cd] p-3">
          <div className="flex items-start gap-2">
            <Icon name="InformationCircleIcon" size={16} className="text-[#c45500] mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-xs font-700 text-[#c45500] mb-0.5">Quick Start — No Documents Needed</p>
              <p className="text-xs text-[#6b4c00]">
                Register with your mobile number. Complete business details after login to unlock all seller features.
              </p>
            </div>
          </div>
        </div>

        {/* Name */}
        <div>
          <label htmlFor="seller-reg-name" className="block text-sm font-600 text-foreground mb-1.5">
            Your Name <span className="text-negative">*</span>
          </label>
          <input
            id="seller-reg-name"
            type="text"
            className={`input-field ${nameError ? 'input-error' : ''}`}
            placeholder="Ravi Kumar"
            value={name}
            onChange={(e) => { setName(e.target.value); if (nameError) setNameError(''); }}
          />
          {nameError && <p className="text-xs text-negative mt-1">{nameError}</p>}
        </div>

        {/* Phone */}
        <div>
          <label htmlFor="seller-reg-phone" className="block text-sm font-600 text-foreground mb-1.5">
            Mobile Number <span className="text-negative">*</span>
          </label>
          <div className="flex gap-2">
            <div className="flex items-center px-3 rounded-lg border border-border bg-muted text-sm text-muted-foreground font-600 select-none">
              +91
            </div>
            <input
              id="seller-reg-phone"
              type="tel"
              autoComplete="tel"
              className={`input-field flex-1 ${phoneError ? 'input-error' : ''}`}
              placeholder="98765 43210"
              inputMode="numeric"
              value={phone}
              onChange={(e) => {
                const val = e.target.value.replace(/\D/g, '').slice(0, 10);
                setPhone(val);
                if (phoneError) setPhoneError('');
              }}
              onKeyDown={(e) => { if (e.key === 'Enter') handleSendOtp(); }}
            />
          </div>
          {phoneError && <p className="text-xs text-negative mt-1">{phoneError}</p>}
          <p className="text-xs text-muted-foreground mt-1.5">
            A 6-digit OTP will be sent to verify your mobile number
          </p>
        </div>

        {/* Register Button */}
        <button
          type="button"
          disabled={isLoading}
          onClick={handleSendOtp}
          className="w-full py-2.5 rounded-lg text-sm font-700 btn-primary flex items-center justify-center gap-2"
          style={{ minHeight: '42px' }}
        >
          {isLoading ? (
            <>
              <Icon name="ArrowPathIcon" size={16} className="animate-spin" />
              <span>Sending OTP...</span>
            </>
          ) : (
            <>
              <Icon name="DevicePhoneMobileIcon" size={16} />
              <span>Register &amp; Send OTP</span>
            </>
          )}
        </button>

        {/* What happens next */}
        <div className="rounded-lg border border-border bg-muted/30 p-3 space-y-2">
          <p className="text-xs font-700 text-foreground">After signing up:</p>
          {[
            { icon: '✅', text: 'Instantly access your seller dashboard' },
            { icon: '📋', text: 'Add business details (name, category, area) at your convenience' },
            { icon: '⚠️', text: 'Account will be suspended if business details are not completed within 7 days' },
          ].map((item, i) => (
            <div key={i} className="flex items-start gap-2">
              <span className="text-sm flex-shrink-0">{item.icon}</span>
              <p className="text-xs text-muted-foreground">{item.text}</p>
            </div>
          ))}
        </div>
      </div>

      <OtpModal
        open={otpOpen}
        phone={phone}
        onClose={() => setOtpOpen(false)}
        onVerified={handleOtpVerified}
        isSellerRegister
        sellerName={name}
      />
    </>
  );
}